import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { 
  Crown, Music, Gift, Heart, Check, ArrowRight, MapPin, Phone,
  Flower2, CreditCard, ShoppingBag, Play, Star, Cake, GraduationCap,
  PartyPopper, Sparkles, Camera, Clock, Users, Calendar, Timer
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { API } from "../App";
import { toast } from "sonner";

const CMC_LOGO = "https://customer-assets.emergentagent.com/job_4c248649-c7ad-49ab-8962-bf67e4ed6b65/artifacts/33twacgu_IMG_0221.png";

// Van photos
const VAN_EXTERIOR = "https://customer-assets.emergentagent.com/job_d1bbb4e0-9a95-44c0-96ba-d2f3b277900c/artifacts/ci6tov60_IMG_0205.jpeg";
const VAN_INTERIOR_1 = "https://customer-assets.emergentagent.com/job_d1bbb4e0-9a95-44c0-96ba-d2f3b277900c/artifacts/chpbjqs1_IMG_2052.jpeg";
const VAN_INTERIOR_2 = "https://customer-assets.emergentagent.com/job_d1bbb4e0-9a95-44c0-96ba-d2f3b277900c/artifacts/lujypwoq_IMG_2051.jpeg";

const fadeInUp = {
  initial: { opacity: 0, y: 40 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] }
};

// Updated packages with SALE pricing
const packages = [
  {
    tier: "pullup",
    name: "Celebration Pull-Up",
    price: 175,
    originalPrice: 225,
    duration: "45 minutes",
    features: [
      "Grand musical arrival with lights & effects",
      "Birthday shoutout & dedication",
      "HD screens + premium surround sound",
      "Photo opportunity with the van",
      "Perfect for surprise birthdays and quick celebrations"
    ],
    icon: Gift,
    onSale: true
  },
  {
    tier: "serenade",
    name: "Serenade Celebration",
    price: 299,
    originalPrice: 350,
    duration: "60 minutes",
    features: [
      "Private lounge celebration experience",
      "Customized music playlist",
      "Personal shoutouts & dedications",
      "Photo & video session included",
      "Personalized moments for your guest",
      "Social media-ready content"
    ],
    icon: Music,
    popular: true,
    onSale: true
  },
  {
    tier: "vip",
    name: "VIP Milestone Experience",
    price: 450,
    originalPrice: 575,
    duration: "90 minutes",
    features: [
      "Full luxury mobile lounge experience",
      "Premium sound system & custom lighting",
      "Up to 6 guests inside the van",
      "Custom playlist & live DJ mixing",
      "Professional photo booth session",
      "Complimentary decorations package",
      "Perfect for milestone events"
    ],
    icon: Crown,
    onSale: true
  }
];

const addOns = [
  { id: "extended", name: "Extended Time (+30 min)", price: 100, icon: Timer },
  { id: "flowers", name: "Bouquet of Flowers", price: 45, icon: Flower2 },
  { id: "card", name: "Personalized Card", price: 15, icon: Heart },
  { id: "basket", name: "Gift Basket", price: 75, icon: ShoppingBag },
  { id: "giftcard", name: "Gift Card", price: 50, icon: CreditCard },
  { id: "song", name: "Personalized Song", price: 125, icon: Music }
];

const celebrationTypes = [
  { icon: Cake, name: "Birthday Surprises", description: "Make their special day unforgettable" },
  { icon: Heart, name: "Proposals", description: "Pop the question in style" },
  { icon: GraduationCap, name: "Graduations", description: "Celebrate their achievement" },
  { icon: Sparkles, name: "Anniversaries", description: "Honor your love story" },
  { icon: PartyPopper, name: "Girls Night Out", description: "Start the party right" },
  { icon: Crown, name: "Milestone Birthdays", description: "40th, 50th & beyond" }
];

const testimonials = [
  {
    name: "Tameka J.",
    rating: 5,
    text: "Best birthday surprise ever! The van pulled up with music and lights and everyone loved it! My mom was in tears of joy.",
    event: "50th Birthday Surprise"
  },
  {
    name: "Marcus D.",
    rating: 5,
    text: "This was the highlight of our anniversary night. The whole neighborhood came out to see what was going on!",
    event: "Anniversary Celebration"
  },
  {
    name: "Ashley R.",
    rating: 5,
    text: "My daughter's graduation surprise was AMAZING! The photos and videos are priceless. Worth every penny!",
    event: "Graduation Party"
  }
];

const howItWorks = [
  {
    step: "01",
    title: "Choose Your Package",
    description: "Select the celebration experience that fits your occasion and budget.",
    icon: Gift
  },
  {
    step: "02",
    title: "We Arrive in Style",
    description: "Our luxury van pulls up with music, lights, and celebration energy.",
    icon: Music
  },
  {
    step: "03",
    title: "Surprise & Celebrate",
    description: "The guest of honor is surprised and enjoys the mobile lounge experience.",
    icon: PartyPopper
  },
  {
    step: "04",
    title: "Capture Memories",
    description: "Take photos, videos, and create unforgettable memories to share.",
    icon: Camera
  }
];

const ChoiceMobile = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedAddOns, setSelectedAddOns] = useState([]);
  const [bookingForm, setBookingForm] = useState({
    customerName: "",
    customerEmail: "",
    customerPhone: "",
    celebrantName: "",
    eventDate: "",
    eventTime: "",
    eventAddress: "",
    eventCity: "",
    specialRequests: "",
    surpriseEntry: false,
    agreeToTerms: false
  });

  const handlePackageSelect = (pkg) => {
    setSelectedPackage(pkg);
    setShowBookingModal(true);
  };

  const handleAddOnToggle = (addOnId) => {
    setSelectedAddOns(prev => 
      prev.includes(addOnId) 
        ? prev.filter(id => id !== addOnId)
        : [...prev, addOnId]
    );
  };

  const calculateTotal = () => {
    if (!selectedPackage) return 0;
    const addOnsTotal = selectedAddOns.reduce((sum, addOnId) => {
      const addOn = addOns.find(a => a.id === addOnId);
      return sum + (addOn?.price || 0);
    }, 0);
    return selectedPackage.price + addOnsTotal;
  };

  const handleBookingSubmit = async (e) => {
    e.preventDefault();
    
    if (!bookingForm.agreeToTerms) {
      toast.error("Please agree to the terms and conditions");
      return;
    }

    setLoading(true);
    try {
      const bookingData = {
        package_tier: selectedPackage.tier,
        package_name: selectedPackage.name,
        package_price: selectedPackage.price,
        add_ons: selectedAddOns.map(id => {
          const addOn = addOns.find(a => a.id === id);
          return { id, name: addOn.name, price: addOn.price };
        }),
        total_amount: calculateTotal(),
        customer_name: bookingForm.customerName,
        customer_email: bookingForm.customerEmail,
        customer_phone: bookingForm.customerPhone,
        celebrant_name: bookingForm.celebrantName,
        event_date: bookingForm.eventDate,
        event_time: bookingForm.eventTime,
        event_address: bookingForm.eventAddress,
        event_city: bookingForm.eventCity,
        special_requests: bookingForm.specialRequests,
        surprise_entry: bookingForm.surpriseEntry
      };

      const response = await axios.post(`${API}/cmc/bookings`, bookingData);
      
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        toast.success("Booking submitted! We'll contact you shortly.");
        setShowBookingModal(false);
        setSelectedPackage(null);
        setSelectedAddOns([]);
        setBookingForm({
          customerName: "",
          customerEmail: "",
          customerPhone: "",
          celebrantName: "",
          eventDate: "",
          eventTime: "",
          eventAddress: "",
          eventCity: "",
          specialRequests: "",
          surpriseEntry: false,
          agreeToTerms: false
        });
      }
    } catch (error) {
      console.error("Booking error:", error);
      toast.error(error.response?.data?.detail || "Failed to submit booking");
    } finally {
      setLoading(false);
    }
  };

  const scrollToPackages = () => {
    document.getElementById('packages').scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToHowItWorks = () => {
    document.getElementById('how-it-works').scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-obsidian">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-24 pb-16 px-6 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-purple-900/20 via-obsidian to-obsidian"></div>
        <div className="container mx-auto max-w-6xl relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              {/* Logo */}
              <div className="relative w-24 h-24 mb-6">
                <div className="absolute inset-0 bg-gold/20 rounded-full blur-xl"></div>
                <img 
                  src={CMC_LOGO}
                  alt="Choice Mobile Celebrations"
                  className="w-24 h-24 object-contain relative z-10 drop-shadow-[0_0_15px_rgba(212,175,55,0.4)]"
                />
              </div>
              
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4">
                The Celebration
                <span className="block text-gold">Comes To You!</span>
              </h1>
              
              <p className="text-white/70 text-lg mb-4">
                Luxury Mobile Celebration Experiences for
              </p>
              <p className="text-white text-xl mb-6">
                <span className="text-gold">Birthdays</span> • <span className="text-gold">Anniversaries</span> • <span className="text-gold">Proposals</span> • <span className="text-gold">Graduations</span>
              </p>
              
              <div className="flex items-center gap-2 text-white/60 mb-8">
                <MapPin className="w-5 h-5 text-gold" />
                <span>Serving <span className="text-white">Montgomery</span> • <span className="text-white">Wetumpka</span> • <span className="text-white">Troy</span></span>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  onClick={scrollToPackages}
                  className="bg-gold text-black hover:bg-gold-light rounded-full px-8 py-6 text-lg font-bold"
                  data-testid="book-celebration-btn"
                >
                  Book Your Celebration
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                <Button
                  onClick={scrollToHowItWorks}
                  variant="outline"
                  className="border-white/20 text-white hover:bg-white/5 rounded-full px-8 py-6 text-lg"
                >
                  See How It Works
                </Button>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="relative rounded-3xl overflow-hidden border border-white/10">
                <img 
                  src={VAN_EXTERIOR}
                  alt="Choice Mobile Celebration Van"
                  className="w-full h-auto object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-obsidian/60 to-transparent"></div>
                <div className="absolute bottom-4 left-4 right-4 flex gap-2">
                  <span className="bg-gold/90 text-black px-3 py-1 rounded-full text-sm font-medium">🚐 Mobile Celebration</span>
                  <span className="bg-white/90 text-black px-3 py-1 rounded-full text-sm font-medium">🎉 Surprise Experience</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-16 px-6 bg-charcoal/50">
        <div className="container mx-auto max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
              How It Works
            </h2>
            <p className="text-white/60 max-w-2xl mx-auto">
              Book your celebration in 4 simple steps
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {howItWorks.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-obsidian rounded-2xl border border-white/5 p-6 text-center relative"
              >
                <div className="absolute -top-3 -right-3 w-10 h-10 bg-gold rounded-full flex items-center justify-center">
                  <span className="text-black font-bold text-sm">{step.step}</span>
                </div>
                <div className="w-14 h-14 bg-gold/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <step.icon className="w-7 h-7 text-gold" />
                </div>
                <h3 className="font-heading text-lg font-bold text-white mb-2">
                  {step.title}
                </h3>
                <p className="text-white/60 text-sm">
                  {step.description}
                </p>
              </motion.div>
            ))}
          </div>

          {/* Mini Gallery in How It Works */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-12 grid grid-cols-3 gap-4"
          >
            <img src={VAN_EXTERIOR} alt="Van exterior" className="rounded-xl w-full h-40 object-cover" />
            <img src={VAN_INTERIOR_1} alt="Van interior" className="rounded-xl w-full h-40 object-cover" />
            <img src={VAN_INTERIOR_2} alt="Van interior lights" className="rounded-xl w-full h-40 object-cover" />
          </motion.div>
        </div>
      </section>

      {/* Packages Section */}
      <section id="packages" className="py-16 px-6">
        <div className="container mx-auto max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
              Choose Your Celebration Package
            </h2>
            <p className="text-white/60 max-w-2xl mx-auto">
              Select the perfect experience for your special occasion
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            {packages.map((pkg, index) => (
              <motion.div
                key={pkg.tier}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={`relative bg-charcoal rounded-2xl border ${
                  pkg.popular ? 'border-gold' : 'border-white/5'
                } p-6 flex flex-col`}
              >
                {pkg.onSale && (
                  <div className="absolute -top-3 -left-3">
                    <span className="bg-red-500 text-white px-3 py-1 rounded-full text-xs font-bold">
                      SALE
                    </span>
                  </div>
                )}
                {pkg.popular && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <span className="bg-gold text-black px-4 py-1 rounded-full text-sm font-bold flex items-center gap-1">
                      <Star className="w-3 h-3" /> Most Popular
                    </span>
                  </div>
                )}
                
                <div className="w-14 h-14 bg-gold/10 rounded-xl flex items-center justify-center mb-4">
                  <pkg.icon className="w-7 h-7 text-gold" />
                </div>
                
                <h3 className="font-heading text-xl font-bold text-white mb-1">
                  {pkg.name}
                </h3>
                
                <div className="flex items-center gap-2 text-white/60 text-sm mb-4">
                  <Clock className="w-4 h-4" />
                  {pkg.duration}
                </div>
                
                <div className="mb-6">
                  {pkg.onSale && pkg.originalPrice && (
                    <span className="text-white/40 text-lg line-through mr-2">${pkg.originalPrice}</span>
                  )}
                  <p className="text-4xl font-bold text-gold inline">${pkg.price}</p>
                  {pkg.onSale && (
                    <span className="ml-2 text-red-400 text-sm font-medium">Limited Time!</span>
                  )}
                </div>
                
                <ul className="space-y-3 mb-6 flex-grow">
                  {pkg.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-2 text-white/80 text-sm">
                      <Check className="w-4 h-4 text-gold flex-shrink-0 mt-0.5" />
                      {feature}
                    </li>
                  ))}
                </ul>
                
                <Button
                  onClick={() => handlePackageSelect(pkg)}
                  className={`w-full rounded-full py-5 font-bold ${
                    pkg.popular 
                      ? 'bg-gold text-black hover:bg-gold-light' 
                      : 'bg-white/10 text-white hover:bg-white/20'
                  }`}
                  data-testid={`select-${pkg.tier}`}
                >
                  {pkg.popular ? 'Book Now' : 'View Details'}
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Limited Offer Banner */}
      <section className="py-8 px-6">
        <div className="container mx-auto max-w-4xl">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="bg-gradient-to-r from-gold/20 to-purple-900/20 rounded-2xl border border-gold/30 p-6 md:p-8 flex flex-col md:flex-row items-center justify-between gap-6"
          >
            <div className="text-center md:text-left">
              <span className="inline-block bg-gold text-black px-3 py-1 rounded-full text-xs font-bold mb-2">
                LIMITED OFFER
              </span>
              <h3 className="font-heading text-2xl font-bold text-white mb-1">
                Weekday Celebration Special
              </h3>
              <p className="text-white/70">
                Starting at <span className="text-gold text-2xl font-bold">$150</span> for weekday bookings
              </p>
              <p className="text-white/50 text-sm mt-1">Limited availability • Book early</p>
            </div>
            <Button
              onClick={scrollToPackages}
              className="bg-gold text-black hover:bg-gold-light rounded-full px-8 py-4 font-bold whitespace-nowrap"
            >
              Check Availability
              <Calendar className="ml-2 w-4 h-4" />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Perfect For Section */}
      <section className="py-16 px-6 bg-charcoal/50">
        <div className="container mx-auto max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
              Perfect For These Celebrations
            </h2>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {celebrationTypes.map((type, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="bg-obsidian rounded-xl border border-white/5 p-4 text-center hover:border-gold/30 transition-colors"
              >
                <div className="w-12 h-12 bg-gold/10 rounded-full flex items-center justify-center mx-auto mb-3">
                  <type.icon className="w-6 h-6 text-gold" />
                </div>
                <h4 className="font-medium text-white text-sm mb-1">{type.name}</h4>
                <p className="text-white/50 text-xs">{type.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-16 px-6">
        <div className="container mx-auto max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
              See What People Are Saying
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-charcoal rounded-2xl border border-white/5 p-6"
              >
                <div className="flex mb-3">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-gold fill-gold" />
                  ))}
                </div>
                <p className="text-white/80 mb-4 italic">"{testimonial.text}"</p>
                <div className="flex items-center justify-between">
                  <span className="text-white font-medium">{testimonial.name}</span>
                  <span className="text-gold/70 text-sm">{testimonial.event}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Photo Gallery */}
      <section className="py-16 px-6 bg-charcoal/50">
        <div className="container mx-auto max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
              Experience Gallery
            </h2>
            <p className="text-white/60">See inside our luxury celebration van</p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="col-span-2 row-span-2"
            >
              <img 
                src={VAN_INTERIOR_1} 
                alt="Van interior" 
                className="w-full h-full object-cover rounded-2xl border border-white/10"
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <img 
                src={VAN_EXTERIOR} 
                alt="Van exterior" 
                className="w-full h-48 object-cover rounded-2xl border border-white/10"
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <img 
                src={VAN_INTERIOR_2} 
                alt="Van lights" 
                className="w-full h-48 object-cover rounded-2xl border border-white/10"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-16 px-6">
        <div className="container mx-auto max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-gold/20 to-purple-900/20 rounded-3xl border border-gold/30 p-8 md:p-12 text-center"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
              Ready to Surprise Someone Special?
            </h2>
            <p className="text-white/70 max-w-xl mx-auto mb-8">
              Book your mobile celebration experience today and create unforgettable memories.
            </p>
            <Button
              onClick={scrollToPackages}
              className="bg-gold text-black hover:bg-gold-light rounded-full px-10 py-6 text-lg font-bold"
              data-testid="final-cta-btn"
            >
              Book Your Celebration
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <p className="text-white/40 text-sm mt-4">
              Questions? Call us at <a href="tel:8772955905" className="text-gold hover:underline">(877) 295-5905</a>
            </p>
          </motion.div>
        </div>
      </section>

      {/* Booking Modal */}
      <Dialog open={showBookingModal} onOpenChange={setShowBookingModal}>
        <DialogContent className="bg-charcoal border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white font-heading text-2xl">
              Book {selectedPackage?.name}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleBookingSubmit} className="space-y-6 mt-4">
            {/* Your Information */}
            <div className="space-y-4">
              <h3 className="text-white font-medium border-b border-white/10 pb-2">Your Information</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-white/70">Your Name</Label>
                  <Input
                    value={bookingForm.customerName}
                    onChange={(e) => setBookingForm({...bookingForm, customerName: e.target.value})}
                    required
                    className="bg-obsidian border-white/10 text-white mt-1"
                    placeholder="John Smith"
                  />
                </div>
                <div>
                  <Label className="text-white/70">Phone Number</Label>
                  <Input
                    value={bookingForm.customerPhone}
                    onChange={(e) => setBookingForm({...bookingForm, customerPhone: e.target.value})}
                    required
                    className="bg-obsidian border-white/10 text-white mt-1"
                    placeholder="(334) 555-1234"
                  />
                </div>
              </div>
              <div>
                <Label className="text-white/70">Email Address</Label>
                <Input
                  type="email"
                  value={bookingForm.customerEmail}
                  onChange={(e) => setBookingForm({...bookingForm, customerEmail: e.target.value})}
                  required
                  className="bg-obsidian border-white/10 text-white mt-1"
                  placeholder="john@example.com"
                />
              </div>
            </div>

            {/* Celebration Details */}
            <div className="space-y-4">
              <h3 className="text-white font-medium border-b border-white/10 pb-2">Celebration Details</h3>
              <div>
                <Label className="text-white/70">Who's Being Celebrated?</Label>
                <Input
                  value={bookingForm.celebrantName}
                  onChange={(e) => setBookingForm({...bookingForm, celebrantName: e.target.value})}
                  required
                  className="bg-obsidian border-white/10 text-white mt-1"
                  placeholder="Birthday person's name"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-white/70">Event Date</Label>
                  <Input
                    type="date"
                    value={bookingForm.eventDate}
                    onChange={(e) => setBookingForm({...bookingForm, eventDate: e.target.value})}
                    required
                    className="bg-obsidian border-white/10 text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-white/70">Arrival Time</Label>
                  <Input
                    type="time"
                    value={bookingForm.eventTime}
                    onChange={(e) => setBookingForm({...bookingForm, eventTime: e.target.value})}
                    required
                    className="bg-obsidian border-white/10 text-white mt-1"
                  />
                </div>
              </div>
              <div>
                <Label className="text-white/70">Event Address</Label>
                <Input
                  value={bookingForm.eventAddress}
                  onChange={(e) => setBookingForm({...bookingForm, eventAddress: e.target.value})}
                  required
                  className="bg-obsidian border-white/10 text-white mt-1"
                  placeholder="123 Main Street"
                />
              </div>
              <div>
                <Label className="text-white/70">City</Label>
                <Input
                  value={bookingForm.eventCity}
                  onChange={(e) => setBookingForm({...bookingForm, eventCity: e.target.value})}
                  required
                  className="bg-obsidian border-white/10 text-white mt-1"
                  placeholder="Montgomery, Wetumpka, or Troy"
                />
              </div>
            </div>

            {/* Add-Ons */}
            <div className="space-y-4">
              <h3 className="text-white font-medium border-b border-white/10 pb-2">Optional Add-Ons</h3>
              <div className="grid grid-cols-2 gap-3">
                {addOns.map((addOn) => (
                  <div
                    key={addOn.id}
                    onClick={() => handleAddOnToggle(addOn.id)}
                    className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
                      selectedAddOns.includes(addOn.id)
                        ? 'border-gold bg-gold/10'
                        : 'border-white/10 hover:border-white/20'
                    }`}
                  >
                    <addOn.icon className={`w-5 h-5 ${selectedAddOns.includes(addOn.id) ? 'text-gold' : 'text-white/60'}`} />
                    <div className="flex-grow">
                      <p className="text-white text-sm">{addOn.name}</p>
                      <p className="text-gold text-xs">+${addOn.price}</p>
                    </div>
                    {selectedAddOns.includes(addOn.id) && (
                      <Check className="w-4 h-4 text-gold" />
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Special Requests */}
            <div>
              <Label className="text-white/70">Special Requests or Songs</Label>
              <Textarea
                value={bookingForm.specialRequests}
                onChange={(e) => setBookingForm({...bookingForm, specialRequests: e.target.value})}
                className="bg-obsidian border-white/10 text-white mt-1"
                placeholder="Any special songs, messages, or requests..."
                rows={3}
              />
            </div>

            {/* Surprise Entry */}
            <div className="flex items-center gap-3">
              <Checkbox
                id="surprise"
                checked={bookingForm.surpriseEntry}
                onCheckedChange={(checked) => setBookingForm({...bookingForm, surpriseEntry: checked})}
              />
              <Label htmlFor="surprise" className="text-white/80 cursor-pointer">
                This is a surprise! Please arrive quietly.
              </Label>
            </div>

            {/* Total */}
            <div className="bg-obsidian rounded-xl p-4 border border-white/10">
              <div className="flex justify-between items-center">
                <span className="text-white/70">Package: {selectedPackage?.name}</span>
                <span className="text-white">${selectedPackage?.price}</span>
              </div>
              {selectedAddOns.length > 0 && (
                <div className="mt-2 pt-2 border-t border-white/10">
                  {selectedAddOns.map(addOnId => {
                    const addOn = addOns.find(a => a.id === addOnId);
                    return (
                      <div key={addOnId} className="flex justify-between text-sm">
                        <span className="text-white/60">{addOn?.name}</span>
                        <span className="text-white/80">+${addOn?.price}</span>
                      </div>
                    );
                  })}
                </div>
              )}
              <div className="flex justify-between items-center mt-3 pt-3 border-t border-white/10">
                <span className="text-white font-medium">Total</span>
                <span className="text-gold text-2xl font-bold">${calculateTotal()}</span>
              </div>
            </div>

            {/* Terms */}
            <div className="flex items-start gap-3">
              <Checkbox
                id="terms"
                checked={bookingForm.agreeToTerms}
                onCheckedChange={(checked) => setBookingForm({...bookingForm, agreeToTerms: checked})}
              />
              <Label htmlFor="terms" className="text-white/60 text-sm cursor-pointer">
                I agree to the terms and conditions. I understand a 50% deposit is required to confirm my booking, 
                with the remaining balance due on the day of the event.
              </Label>
            </div>

            <Button
              type="submit"
              disabled={loading || !bookingForm.agreeToTerms}
              className="w-full bg-gold text-black hover:bg-gold-light rounded-full py-6 font-bold"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  Proceed to Payment
                  <ArrowRight className="ml-2 w-5 h-5" />
                </>
              )}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
};

export default ChoiceMobile;
